// How many edits a number of gems pays for. ONE place (audit 2026-09-04, #11).
//
// "≈ N editów" is printed on the pricing card, the billing plan card, the
// onboarding welcome and the day-one dashboard panel. Each of them used to do
// its own `Math.floor(gems / cost)` with its own idea of the cost — a literal 3
// in two of them, the server's number in the others — so the day the price of an
// edit moves, the four sentences disagree with each other and with the wallet.
//
// The cost is a PARAMETER, never a default: it comes from the server
// (`/api/render/config` → gemsPerEdit, or the pricing catalogue's gem_costs).
// An unknown or unusable cost returns null, and a null is rendered as "say
// nothing", not as zero — "enough for 0 edits" is a sentence that must not reach
// a customer because the request was slow (see lib/renderConfig.ts).

export function editsFromGems(
  gems: number,
  gemsPerEdit: number | null | undefined,
): number | null {
  if (typeof gemsPerEdit !== 'number' || !Number.isFinite(gemsPerEdit) || gemsPerEdit <= 0) return null
  if (typeof gems !== 'number' || !Number.isFinite(gems)) return null
  return Math.max(0, Math.floor(gems / gemsPerEdit))
}

// ── "low balance" — ONE rule, ONE place (0018, MAPA 02 decision 2) ─────────────
//
// Low = fewer than LOW_BALANCE_EDITS edits left at the server's price, OR less than
// LOW_BALANCE_PLAN_FRACTION of the plan's monthly pool. Billing renders it today;
// the dashboard and the generator are meant to call the same function later, so
// the sentence "the same rule everywhere" stays true by construction.
//
// Both comparisons are STRICT (<): 9 gems at 3 per edit is 3 edits, which is not
// "fewer than 3"; 400 of a 4 000 pool is exactly 10 %, which is not "below 10 %".
// An unknown price returns null — "unknown", never "fine" and never "low".

export const LOW_BALANCE_EDITS = 3
export const LOW_BALANCE_PLAN_FRACTION = 0.1

export interface LowBalanceInput {
  /** Gems on the account (plan + purchased). */
  balance: number
  /** What one edit costs, from the server; null while unknown. */
  gemsPerEdit: number | null | undefined
  /** The plan's monthly pool; 0 / null / undefined = no plan, so no % threshold. */
  planAllowance?: number | null
}

export interface LowBalanceVerdict {
  low: boolean
  /** Edits the balance still pays for at the given price. */
  editsLeft: number
  /** The gem count the edit threshold corresponds to (LOW_BALANCE_EDITS × price). */
  thresholdGems: number
  /** Which rule fired (both may). */
  byEdits: boolean
  byPlanShare: boolean
}

export function lowBalance({ balance, gemsPerEdit, planAllowance }: LowBalanceInput): LowBalanceVerdict | null {
  const editsLeft = editsFromGems(balance, gemsPerEdit)
  if (editsLeft == null) return null
  const price = gemsPerEdit as number
  const byEdits = editsLeft < LOW_BALANCE_EDITS
  const pool = typeof planAllowance === 'number' && Number.isFinite(planAllowance) && planAllowance > 0
    ? planAllowance : 0
  const byPlanShare = pool > 0 && balance < pool * LOW_BALANCE_PLAN_FRACTION
  return {
    low: byEdits || byPlanShare,
    editsLeft,
    thresholdGems: LOW_BALANCE_EDITS * price,
    byEdits,
    byPlanShare,
  }
}
